import { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Loading, Select } from '@/components/ui/daisy';
import useCoursePathAssessments from '@/data/hook/evaluation/useCoursePathAssessments';

import { LessonPlanItemFormFields } from './LessonPlanItemInlineForm';

type LessonPlanItemAssessmentFieldsProps = {
  coursePathId: number;
  courseLineId: number;
};

export default function LessonPlanItemAssessmentFields({
  coursePathId,
  courseLineId,
}: LessonPlanItemAssessmentFieldsProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.lessonPlansTab',
  });

  const {
    register,
    control,
    setValue,
    formState: { errors },
  } = useFormContext<LessonPlanItemFormFields>();

  const assessmentId = useWatch({ control, name: 'assessmentId' });

  const { assessments, isLoading } = useCoursePathAssessments({
    coursePathId,
    courseLineId,
  });

  const assessmentItems = useMemo(
    () => assessments.find(a => a.id === assessmentId)?.items ?? [],
    [assessments, assessmentId],
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-7">
        <Loading variant="spinner" color="primary" />
      </div>
    );
  }

  return (
    <div className="flex gap-4">
      <Select.Fieldset
        className="flex-1"
        label={t('assessment')}
        labelAlt={errors?.assessmentId?.message}
      >
        <Select
          className="w-full"
          color={!errors?.assessmentId?.message ? 'default' : 'error'}
          {...register('assessmentId', {
            setValueAs: value => (!!value ? Number(value) : null),
            validate: value => !!value || t('assessmentRequired'),
            onChange: () => setValue('assessmentItemId', null),
          })}
        >
          <Select.Option value="" disabled>
            {t('assessmentPlaceholder')}
          </Select.Option>
          {assessments.map(assessment => (
            <Select.Option key={assessment.id} value={assessment.id}>
              {assessment.name}
            </Select.Option>
          ))}
        </Select>
      </Select.Fieldset>

      <Select.Fieldset
        className="flex-1"
        label={t('assessmentItem')}
        labelAlt={errors?.assessmentItemId?.message}
      >
        <Select
          className="w-full"
          disabled={!assessmentId}
          color={!errors?.assessmentItemId?.message ? 'default' : 'error'}
          {...register('assessmentItemId', {
            setValueAs: value => (!!value ? Number(value) : null),
            validate: value => !!value || t('assessmentItemRequired'),
          })}
        >
          <Select.Option value="" disabled>
            {t('assessmentItemPlaceholder')}
          </Select.Option>
          {assessmentItems.map(item => (
            <Select.Option key={item.id} value={item.id}>
              {item.name}
            </Select.Option>
          ))}
        </Select>
      </Select.Fieldset>
    </div>
  );
}
